import React, { useState, useEffect } from 'react';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import NavigationBreadcrumb from '../../components/ui/NavigationBreadcrumb';
import Icon from '../../components/AppIcon';
import ActivePlanSection from './components/ActivePlanSection';
import UsageMetricsSection from './components/UsageMetricsSection';
import InvoiceTable from './components/InvoiceTable';
import UserAccessTab from './components/UserAccessTab';
import AccountTab from './components/AccountTab';
import PhoneNumbersSection from './components/PhoneNumbersSection';
import WalletBalanceSection from './components/WalletBalanceSection';
import api from '../../api/axios';

// Check BEFORE component mounts
const paymentReturnParams = new URLSearchParams(window.location.search);
const isPhonePaymentReturn = paymentReturnParams.get('payment') === 'complete';
const isWalletPaymentReturn = paymentReturnParams.get('wallet_payment') === 'complete';

// Only auto-close for phone number payment (new tab flow)
if (isPhonePaymentReturn) {
  window.close();
}

const Settings = () => {
  const [activeTab, setActiveTab] = useState('billing');
  const [balanceData, setBalanceData] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [currentUserRole, setCurrentUserRole] = useState('');
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [showCloseMessage, setShowCloseMessage] = useState(isPhonePaymentReturn);
  const [walletReturnReady, setWalletReturnReady] = useState(!isWalletPaymentReturn);
  const [walletVerifying, setWalletVerifying] = useState(isWalletPaymentReturn || !!localStorage.getItem('wallet_pending_order'));
  const [walletPaymentResult, setWalletPaymentResult] = useState(null); // { status: 'PAID'|'FAILED', amount }
  const [walletRefreshKey, setWalletRefreshKey] = useState(0);

  useEffect(() => {
    if (isPhonePaymentReturn) {
      setShowCloseMessage(true);
      return;
    }

    // Handle wallet payment return or pending order from localStorage
    if (isWalletPaymentReturn || localStorage.getItem('wallet_pending_order')) {
      verifyWalletPayment().then(() => {
        setWalletReturnReady(true);
        if (isWalletPaymentReturn) cleanUpWalletReturn();
      });
    }

    fetchBalance();
    fetchCurrentUserRole();
  }, []);

  const verifyWalletPayment = async () => {
    // Get order ID and amount from localStorage or URL
    let orderId = null;
    let orderAmount = null;
    let orderGstAmount = null;
    let orderTotalAmount = null;
    const pendingOrder = localStorage.getItem('wallet_pending_order');
    if (pendingOrder) {
      try {
        const parsed = JSON.parse(pendingOrder);
        orderId = parsed.orderId;
        orderAmount = parsed.amount;
        orderGstAmount = parsed.gstAmount ?? null;
        orderTotalAmount = parsed.totalAmount ?? null;
      } catch (e) { /* ignore */ }
    }
    if (!orderId) {
      orderId = paymentReturnParams.get('order_id');
    }

    if (!orderId) {
      setWalletVerifying(false);
      return;
    }

    try {
      let finalStatus = null;
      let finalAmount = orderAmount;
      let finalGstAmount = orderGstAmount;
      let finalTotalAmount = orderTotalAmount;

      for (let i = 0; i < 5; i++) {
        const res = await api.get(`/api/v1/wallet/order/${orderId}/status`);
        const data = res.data?.data || {};
        const status = data.status;
        if (data.baseAmount != null) finalAmount = data.baseAmount;
        else if (data.amount != null) finalAmount = data.amount;
        if (data.gstAmount != null) finalGstAmount = data.gstAmount;
        if (data.totalAmount != null) finalTotalAmount = data.totalAmount;
        if (status === 'PAID' || status === 'FAILED' || status === 'EXPIRED') {
          finalStatus = status;
          break;
        }
        if (i < 4) {
          await new Promise(r => setTimeout(r, 2000));
        }
      }
      setWalletPaymentResult({
        status: finalStatus === 'PAID' ? 'PAID' : 'FAILED',
        amount: finalAmount,
        gstAmount: finalGstAmount,
        totalAmount: finalTotalAmount,
      });
    } catch (err) {
      console.error('Wallet payment verification error:', err);
      setWalletPaymentResult({
        status: 'FAILED',
        amount: orderAmount,
        gstAmount: orderGstAmount,
        totalAmount: orderTotalAmount,
      });
    }

    setWalletVerifying(false);
    localStorage.removeItem('wallet_pending_order');
  };

  const cleanUpWalletReturn = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('wallet_payment');
    url.searchParams.delete('order_id');
    window.history.replaceState({}, '', url.pathname + (url.search || ''));
  };

  const fetchCurrentUserRole = async () => {
    try {
      const response = await api.get('/api/v1/settings/account');
      setCurrentUserRole(response.data?.data?.role || '');
      setCurrentUserEmail(response.data?.data?.email || '');
    } catch (error) {
      console.error('Failed to fetch user role:', error);
    }
  };

  const fetchBalance = async () => {
    try {
      const response = await api.get('/api/v1/dashboard/balance');
      setBalanceData(response.data?.data || null);
    } catch (error) {
      console.error('Failed to fetch balance:', error);
    } finally {
      setBalanceLoading(false);
    }
  };

  const tabs = [
    { id: 'billing', label: 'Billing', icon: 'CreditCard' },
    { id: 'user-access', label: 'User Access', icon: 'Users' },
    { id: 'account', label: 'Account', icon: 'User' }
  ];

  const breadcrumbItems = [
    { label: 'Home', path: '/analytics-overview' },
    { label: 'Settings', path: '/settings' }
  ];

  // Phone number payment return tab — show close message
  if (showCloseMessage) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center p-8 max-w-sm">
          <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle" size={32} className="text-green-500" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Payment Complete</h2>
          <p className="text-sm text-gray-500 mb-6">
            You can close this tab and return to the original page. Your number will be activated automatically.
          </p>
          <button
            onClick={() => window.close()}
            className="px-5 py-2.5 bg-[#2F55FF] text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors"
          >
            Close this tab
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <PrimaryNavigation />

      <div className="main-content">
        <div className="p-4 md:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6">
            <NavigationBreadcrumb items={breadcrumbItems} />
            <div className="flex items-center gap-3 mt-4">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Icon name="Settings" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground">Settings</h1>
                <p className="text-sm text-muted-foreground mt-1">Manage your account, billing, and preferences</p>
              </div>
            </div>
          </div>

          {/* Horizontal Tab Navigation */}
          <div className="bg-card border border-border rounded-lg shadow-sm">
            <div className="border-b border-border overflow-x-auto">
              <div className="flex gap-1 p-2 min-w-max">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${
                      activeTab === tab?.id
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-4 md:p-6">
              {activeTab === 'billing' && (
                <div className="space-y-6">
                  <WalletBalanceSection key={`${walletReturnReady ? 'ready' : 'pending'}-${walletRefreshKey}`} />
                  <PhoneNumbersSection onBalanceChange={() => setWalletRefreshKey((k) => k + 1)} />
                  {/* <ActivePlanSection balanceData={balanceData} loading={balanceLoading} /> */}
                  {/* <UsageMetricsSection balanceData={balanceData} loading={balanceLoading} /> */}
                  <InvoiceTable />
                </div>
              )}

              {activeTab === 'user-access' && (
                <UserAccessTab currentUserRole={currentUserRole} currentUserEmail={currentUserEmail} />
              )}

              {activeTab === 'account' && (
                <AccountTab />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Wallet payment verifying loader */}
      {walletVerifying && !walletPaymentResult && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-8 text-center">
            <div className="w-12 h-12 border-[3px] border-gray-200 border-t-[#2F55FF] rounded-full animate-spin mx-auto mb-5" />
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Verifying Payment</h2>
            <p className="text-sm text-gray-500">Please wait while we confirm your payment...</p>
          </div>
        </div>
      )}

      {/* Wallet payment result popup */}
      {walletPaymentResult && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setWalletPaymentResult(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-8 text-center">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
              walletPaymentResult.status === 'PAID' ? 'bg-green-50' : 'bg-red-50'
            }`}>
              <Icon
                name={walletPaymentResult.status === 'PAID' ? 'CheckCircle' : 'XCircle'}
                size={32}
                className={walletPaymentResult.status === 'PAID' ? 'text-green-500' : 'text-red-500'}
              />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              {walletPaymentResult.status === 'PAID' ? 'Payment Successful' : 'Payment Failed'}
            </h2>
            {walletPaymentResult.status === 'PAID' ? (
              <div className="mb-6">
                <p className="text-sm text-gray-500">
                  {`₹${Number(walletPaymentResult.amount || 0).toLocaleString('en-IN')} has been added to your wallet.`}
                </p>
                {walletPaymentResult.totalAmount != null && walletPaymentResult.gstAmount != null && (
                  <p className="text-xs text-gray-400 mt-1">
                    {`Paid ₹${Number(walletPaymentResult.totalAmount).toLocaleString('en-IN')} incl. ₹${Number(walletPaymentResult.gstAmount).toLocaleString('en-IN')} GST.`}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 mb-6">
                Your payment could not be processed. Please try again.
              </p>
            )}
            <button
              onClick={() => setWalletPaymentResult(null)}
              className={`px-5 py-2.5 text-sm font-medium rounded-xl transition-colors ${
                walletPaymentResult.status === 'PAID'
                  ? 'bg-[#2F55FF] text-white hover:bg-blue-700'
                  : 'bg-red-600 text-white hover:bg-red-700'
              }`}
            >
              {walletPaymentResult.status === 'PAID' ? 'Done' : 'Close'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
